import axios from 'axios';
import type { Game } from '../types';

const isDevelopment = import.meta.env.DEV;
const baseURL = isDevelopment ? '/api' : '/.netlify/functions';

const api = axios.create({
  baseURL,
  timeout: 15000,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

function sanitizeGameData(data: any): Game {
  return {
    id: Number(data.id) || 0,
    name: String(data.name || ''),
    playerCount: Number(data.playerCount) || 0,
    peakToday: Number(data.peakToday) || 0,
    peakAllTime: Number(data.peakAllTime) || 0,
    categories: Array.isArray(data.categories) ? data.categories.map(String) : [],
    imageUrl: String(data.imageUrl || ''),
    description: String(data.description || ''),
    releaseDate: String(data.releaseDate || ''),
    developer: String(data.developer || ''),
    publisher: String(data.publisher || ''),
    rating: data.rating ? {
      positive: Number(data.rating.positive) || 0,
      negative: Number(data.rating.negative) || 0,
      total: Number(data.rating.total) || 0,
      percentage: Number(data.rating.percentage) || 0
    } : undefined,
    monthlyStats: Array.isArray(data.monthlyStats) ? data.monthlyStats.map(stat => ({
      month: String(stat.month || ''),
      averagePlayers: Number(stat.averagePlayers) || 0,
      peakPlayers: Number(stat.peakPlayers) || 0,
      gain: Number(stat.gain) || 0,
      gainPercentage: Number(stat.gainPercentage) || 0
    })) : undefined
  };
}

export async function fetchGames(searchQuery?: string): Promise<Game[]> {
  try {
    const params = searchQuery ? { q: searchQuery } : undefined;
    const response = await api.get('/getTopGames', { params });

    if (!response.data) {
      console.error('Empty response from API');
      return [];
    }

    if (Array.isArray(response.data)) {
      return response.data.map(game => sanitizeGameData(game));
    }

    if (response.data.error) {
      throw new Error(response.data.error);
    }

    console.error('Invalid response format:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching games:', error);
    throw error;
  }
}

export async function fetchGameDetails(id: number): Promise<Game> {
  try {
    const response = await api.get(`/getGameDetails/${id}`);

    if (!response.data) {
      throw new Error('Game not found');
    }

    return sanitizeGameData(response.data);
  } catch (error) {
    console.error('Error fetching game details:', error);
    throw error;
  }
}